/**
 * @file variant-matrix.ts
 *
 * Pure helpers for the client-side combination preview: generating the cartesian of the
 * option axes and reconciling the per-variant table rows to it, so a new combination
 * appears the moment the merchant defines it — editable, tagged "Unsaved" — instead of
 * only after a save round-trip. Mirrors the engine's generation semantics: a
 * combination's identity is the UNORDERED set of its folded `(axis, value)` pairs.
 *
 * A not-yet-saved combination carries a synthetic `new:`-prefixed row id derived from its
 * value-set key, so the row keeps its identity (and the merchant's buffered edits) across
 * regenerations; a persisted variant keeps its engine uuid. The write path already treats
 * the two correctly with no change: a baseline-less row rides a structural save as a full
 * `variants[]` override and is skipped by the id-addressed batch.
 *
 * **Suppression.** The admin read path never sees retired variants, so a combination
 * expressible under the axes AS LOADED but absent from the loaded rows is a previously
 * retired one — previewing it (or sending an override for it) would offer to resurrect a
 * variant the merchant deleted. Those combinations are captured at seed time
 * ({@link suppressedComboKeys}) and skipped by the reconcile for the life of the form.
 * A combination involving a value added THIS session is genuinely new and always previews.
 */

import type { ShopProductAttribute, ShopProductVariant } from '../../api/shop-types';
import { foldOptionValue } from './product-form-utils';

/** Prefix on the synthetic id of a combination that has no server variant yet. */
export const NEW_VARIANT_ID_PREFIX = 'new:';

/** One `{ name, value }` combination selector, as carried on a variant row. */
type Selector = ShopProductVariant['options'][number];

/**
 * Whether a table row is a not-yet-saved combination (client-generated preview) rather
 * than a persisted variant. A preview row cannot be retired (nothing exists server-side)
 * and never rides the id-addressed batch write.
 *
 * @param row - The variant table row.
 *
 * @returns `true` when the row has no server variant behind it yet.
 */
export function isNewVariantRow(row: ShopProductVariant): boolean {
  return row.id.startsWith(NEW_VARIANT_ID_PREFIX);
}

/**
 * A canonical key for a combination's unordered set of folded `(axis, value)` pairs —
 * the same fold the engine matches values by ({@link foldOptionValue}). Two combinations
 * built from the same pairs in any axis order share a key. The axis name is part of each
 * pair so two axes sharing a vocabulary (a Primary and a Secondary colour both offering
 * Red) cannot collide; each pair is JSON-encoded so a value cannot run into an axis name.
 *
 * @param options - The combination's selectors.
 *
 * @returns The order-independent identity key.
 */
export function variantComboKey(options: readonly Selector[]): string {
  return options
    .map((sel) => JSON.stringify([foldOptionValue(sel.name), foldOptionValue(sel.value)]))
    .sort()
    // Write the separator as an ESCAPE, never as a literal control byte: a raw NUL in the
    // source makes git classify the whole module as binary, so diffs, blame and MR review
    // degrade to "Binary files differ". Matches `overrideKey` in the engine's product.service.
    .join('\u0000');
}

/**
 * The cartesian product of the axes' values — one selector list per combination, in axis
 * order. An axis with no name or no values contributes nothing, so a half-filled row in
 * the option editor never generates rows.
 *
 * Callers gate on `countCombinations` before generating, so an over-cap matrix is never
 * built.
 *
 * @param attributes - The product's variation axes.
 *
 * @returns One selector list per combination (empty when there is no usable axis).
 */
export function generateCombinations(attributes: ShopProductAttribute[]): Selector[][] {
  const axes = attributes.filter((a) => a.name && a.options.length > 0);
  if (axes.length === 0) {
    return [];
  }
  return axes.reduce<Selector[][]>(
    (combos, axis) =>
      combos.flatMap((combo) =>
        axis.options.map((option) => [...combo, { name: axis.name, value: option.value }]),
      ),
    [[]],
  );
}

/**
 * The combinations that must NOT be previewed: expressible under the axes as loaded but
 * absent from the loaded rows — i.e. previously retired variants, which the admin read
 * path filters out. Captured once at seed time and carried (relabelled with the axes)
 * for the life of the form, so the preview never offers to resurrect a deleted variant.
 *
 * @param attributes - The axes as loaded.
 * @param rows - The variant rows as loaded (the sellable set).
 *
 * @returns The suppressed combinations' selector lists.
 */
export function suppressedCombinations(
  attributes: ShopProductAttribute[],
  rows: ShopProductVariant[],
): Selector[][] {
  const present = new Set(rows.map((row) => variantComboKey(row.options)));
  return generateCombinations(attributes).filter(
    (combo) => !present.has(variantComboKey(combo)),
  );
}

/** The display label of a combination — its values in axis order, e.g. `Red / L`. */
function comboLabel(options: Selector[]): string {
  return options.map((sel) => sel.value).join(' / ');
}

/** Whether every folded pair of `subset` also appears in `superset`. */
function isValueSubset(subset: readonly Selector[], superset: readonly Selector[]): boolean {
  const pairs = new Set(
    superset.map((sel) =>
      JSON.stringify([foldOptionValue(sel.name), foldOptionValue(sel.value)]),
    ),
  );
  return subset.every((sel) =>
    pairs.has(JSON.stringify([foldOptionValue(sel.name), foldOptionValue(sel.value)])),
  );
}

/**
 * A fresh preview row for a combination, seeded from an optional source row's values.
 * The image is never inherited — a new combination starts without one rather than
 * silently reusing a sibling's picture (and the gallery invariant would make a wrong
 * inheritance a write error). A SKU is unique, so it rides only when the caller says so.
 *
 * @param options - The combination's selectors.
 * @param source - The row to inherit price/sale/stock/weight from, if any.
 * @param withSku - Whether the source's SKU rides along (at most one successor may).
 *
 * @returns The seeded preview row, carrying the synthetic `new:` id.
 */
function seedRow(
  options: Selector[],
  source: ShopProductVariant | undefined,
  withSku: boolean,
): ShopProductVariant {
  return {
    id: NEW_VARIANT_ID_PREFIX + variantComboKey(options),
    options,
    label: comboLabel(options),
    regular_price: source?.regular_price ?? '',
    sale_price: source?.sale_price ?? '',
    sku: withSku && source ? source.sku : '',
    weight: source?.weight ?? '',
    manage_stock: source?.manage_stock ?? true,
    stock_quantity: source ? source.stock_quantity : 0,
    image: null,
    enabled: true,
  };
}

/**
 * Reconciles the per-variant table rows to the axes' combinations, in generated order:
 * a surviving combination keeps its row (id, values, buffered edits — matched by
 * unordered folded value set); a suppressed combination is skipped (see the module
 * docblock); a genuinely new combination is seeded by projecting the merchant's
 * per-variant work across the change rather than resetting to blanks:
 *
 * - **Axis added** — the new combination strips back to the enabled row it expands (its
 *   selectors minus the added axes) and inherits that parent's price, sale price, stock,
 *   and weight. The parent's SKU rides only the one successor carrying each added axis's
 *   FIRST value (a SKU cannot be duplicated across the fan-out).
 * - **Axis removed** — several prior combinations fold onto one; the survivor takes the
 *   first enabled row whose values are a superset of it, SKU included.
 * - **Neither** (a value added to an existing axis, or the first axis of a fresh
 *   product) — seeded from the first enabled row (blank SKU), or blank on a product with
 *   no rows yet.
 *
 * Renames never reach this function as changes: the caller relabels the rows first
 * (`relabelRowSelections`), so a renamed combination arrives already matching its row.
 * A row whose combination no longer exists is dropped, exactly as the engine
 * soft-deletes the variant on save.
 *
 * @param attributes - The axes to generate from.
 * @param rows - The current table rows (already relabelled for id-preserving renames).
 * @param suppressed - Combinations never to preview (retired at load), as selector lists.
 *
 * @returns The reconciled rows, in generated-combination order.
 */
export function syncRowsToCombinations(
  attributes: ShopProductAttribute[],
  rows: ShopProductVariant[],
  suppressed: readonly Selector[][] = [],
): ShopProductVariant[] {
  const rowByKey = new Map(rows.map((row) => [variantComboKey(row.options), row]));
  const suppressedKeys = new Set(suppressed.map((combo) => variantComboKey(combo)));
  const liveRows = rows.filter((row) => row.enabled);
  const representative = liveRows[0];

  const rowAxisNames = new Set(
    liveRows.flatMap((row) => row.options.map((sel) => foldOptionValue(sel.name))),
  );
  const addedAxes = attributes.filter(
    (axis) =>
      axis.name && axis.options.length > 0 && !rowAxisNames.has(foldOptionValue(axis.name)),
  );
  const addedAxisNames = new Set(addedAxes.map((axis) => foldOptionValue(axis.name)));
  const firstValueByAddedAxis = new Map(
    addedAxes.map((axis) => [
      foldOptionValue(axis.name),
      foldOptionValue(axis.options[0]?.value ?? ''),
    ]),
  );
  const liveByKey = new Map(liveRows.map((row) => [variantComboKey(row.options), row]));

  return generateCombinations(attributes).flatMap((combo) => {
    const key = variantComboKey(combo);
    const survivor = rowByKey.get(key);
    if (survivor) {
      // The survivor keeps everything but takes the freshly generated selectors — the
      // same values, but consistently ordered and labelled by the current axes.
      return [{ ...survivor, options: combo, label: comboLabel(combo) }];
    }
    if (suppressedKeys.has(key)) {
      return [];
    }

    if (addedAxisNames.size > 0) {
      const parentSelectors = combo.filter(
        (sel) => !addedAxisNames.has(foldOptionValue(sel.name)),
      );
      const parent = liveByKey.get(variantComboKey(parentSelectors));
      if (parent) {
        const carriesFirstOfEachAddedAxis = combo.every(
          (sel) =>
            !addedAxisNames.has(foldOptionValue(sel.name)) ||
            foldOptionValue(sel.value) === firstValueByAddedAxis.get(foldOptionValue(sel.name)),
        );
        return [seedRow(combo, parent, carriesFirstOfEachAddedAxis)];
      }
    }

    // Axis removed: several prior combinations fold onto this one — take the first
    // enabled row (in table order, so deterministic) whose values are a superset.
    const supersetParent = liveRows.find((row) => isValueSubset(combo, row.options));
    if (supersetParent) {
      return [seedRow(combo, supersetParent, true)];
    }

    return [seedRow(combo, representative, false)];
  });
}

/**
 * Gives a preview row back the persisted variant it turns out to be.
 *
 * A combination can leave and re-enter the matrix within one editing session — add an
 * axis and remove it again — and the row that comes out the other side is rebuilt by the
 * seeder, so it carries a synthetic id even though the variant behind it never went
 * anywhere. Left that way it is invisible to the save: the id-addressed batch skips
 * baseline-less rows, so an edit made after the round trip would be dropped without a
 * word (and the row could not be retired). Restoring the baseline id routes it back
 * through the ordinary diff-against-baseline machinery.
 *
 * The row's CURRENT values are kept (they are the merchant's buffer, projected or
 * edited) with one exception: a `null` image is always a projection artifact — the
 * seeder nulls images unconditionally and by design — so the persisted image is
 * restored rather than letting the reclaim read as a silent image deletion. A non-null
 * image is the merchant's own mid-trip pick and survives.
 *
 * @param rows - The reconciled table rows.
 * @param baseline - The pre-edit rows (from the loaded product).
 *
 * @returns The rows, with any that are really a persisted variant re-keyed to it.
 */
export function reclaimPersistedRows(
  rows: ShopProductVariant[],
  baseline: ShopProductVariant[],
): ShopProductVariant[] {
  const baselineByKey = new Map(baseline.map((row) => [variantComboKey(row.options), row]));
  return rows.map((row) => {
    if (!isNewVariantRow(row)) {
      return row;
    }
    const persisted = baselineByKey.get(variantComboKey(row.options));
    if (!persisted) {
      return row;
    }
    return {
      ...row,
      id: persisted.id,
      image: row.image ?? persisted.image,
      enabled: persisted.enabled,
    };
  });
}
